// src/pages/CheckoutStub.jsx
/* eslint-disable max-len */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import { getFunctions, httpsCallable } from "firebase/functions";

const norm = (v) => String(v ?? "").trim();
const lower = (v) => norm(v).toLowerCase();

const isPaidStatus = (s) => lower(s) === "paid";
const isPendingStatus = (s) => lower(s) === "pending";
const isFailedStatus = (s) => lower(s) === "failed";
const isCancelledStatus = (s) => lower(s) === "cancelled";

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "N/A";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const PAYFAST_MODE = lower(process.env.REACT_APP_PAYFAST_MODE || "sandbox");

const PAYFAST_ENDPOINTS = {
  sandbox: "https://sandbox.payfast.co.za/eng/process",
  live: "https://www.payfast.co.za/eng/process",
};

function isTradeSafeSandboxCheckoutEnabled() {
  const target = lower(process.env.REACT_APP_FIREBASE_TARGET || "");
  const flag = lower(process.env.REACT_APP_TRADESAFE_SANDBOX_CHECKOUT_ENABLED || "");
  return target === "sandbox" && flag === "true";
}

const TRADESAFE_SANDBOX_HOSTS = new Set([
  "pay-sandbox.tradesafe.dev",
  "checkout.tradesafe.dev",
  "pay.tradesafe.dev",
  "app.tradesafe.dev",
]);

const SENSITIVE_QUERY_PARAM_FRAGMENTS = new Set([
  "token",
  "secret",
  "auth",
  "key",
  "password",
  "credential",
  "session",
]);

function isSafeTradeSafeRedirectUrl(url) {
  if (!url || typeof url !== "string") return false;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== "https:") return false;
  if (!TRADESAFE_SANDBOX_HOSTS.has(parsed.hostname)) return false;
  if (parsed.username || parsed.password) return false;

  for (const param of parsed.searchParams.keys()) {
    const lowerParam = lower(param);
    for (const fragment of SENSITIVE_QUERY_PARAM_FRAGMENTS) {
      if (lowerParam.includes(fragment)) return false;
    }
  }

  return true;
}

function postToGateway({ endpoint, fields, tradeId, mPaymentId }) {
  // Persist for Return/Cancel fallback (PayFast sometimes returns without your custom params)
  try {
    if (tradeId) sessionStorage.setItem("pf_last_tradeId", tradeId);
    if (mPaymentId) sessionStorage.setItem("pf_last_mPaymentId", mPaymentId);
  } catch (e) {
    // ignore
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = endpoint;
  form.style.display = "none";

  Object.entries(fields || {}).forEach(([k, v]) => {
    const val = String(v ?? "").trim();
    if (!val) return; // skip empty
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = val;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

const CheckoutStub = () => {
  const { tradeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const [trade, setTrade] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);

  const [startingPayment, setStartingPayment] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const useTradeSafeSandbox = isTradeSafeSandboxCheckoutEnabled();

  const callFn = useCallback(async (name, payload) => {
    const functions = getFunctions(undefined, "us-central1");
    const fn = httpsCallable(functions, name);
    return await fn(payload);
  }, []);

  const paymentProvider = useMemo(() => lower(workflow?.payment?.provider || ""), [workflow]);
  const paymentStatus = useMemo(() => lower(workflow?.payment?.status || ""), [workflow]);
  const workflowStage = useMemo(() => lower(workflow?.stage || ""), [workflow]);

  const isAwaitingPaymentStage = useMemo(
    () => workflowStage === "awaiting_payment",
    [workflowStage]
  );

  const amountDue = useMemo(() => {
    // 1) Once payment has been initiated, this is authoritative
    const wfPay = workflow?.payment?.amountZar;
    if (Number.isFinite(Number(wfPay)) && Number(wfPay) > 0) return Number(wfPay);

    // 2) Use buyer payable amount when delivery is included in checkout pricing.
    const wfCashBuyerPayable = workflow?.cash?.buyerPayableAmountZar;
    if (Number.isFinite(Number(wfCashBuyerPayable)) && Number(wfCashBuyerPayable) > 0) return Number(wfCashBuyerPayable);

    const wfCashDealBuyerPayable = workflow?.cashDeal?.buyerPayableAmountZar;
    if (Number.isFinite(Number(wfCashDealBuyerPayable)) && Number(wfCashDealBuyerPayable) > 0) return Number(wfCashDealBuyerPayable);

    // 3) Canonical accepted cash amount (present BEFORE createPayfastPayment)
    const wfCash = workflow?.cash?.amountGross;
    if (Number.isFinite(Number(wfCash)) && Number(wfCash) > 0) return Number(wfCash);

    // 4) Your “cashDeal” structure also has the same number
    const wfCashDeal = workflow?.cashDeal?.amountZar;
    if (Number.isFinite(Number(wfCashDeal)) && Number(wfCashDeal) > 0) return Number(wfCashDeal);

    // 5) LAST resort only (listing price)
    const t = trade || {};
    const tradeFallback = t?.expectedCashAmount ?? t?.estimatedValue ?? null;
    if (Number.isFinite(Number(tradeFallback)) && Number(tradeFallback) > 0) return Number(tradeFallback);

    return null;
  }, [workflow, trade]);

  const paid = useMemo(() => isPaidStatus(paymentStatus), [paymentStatus]);
  const pending = useMemo(() => isPendingStatus(paymentStatus), [paymentStatus]);
  const failed = useMemo(() => isFailedStatus(paymentStatus), [paymentStatus]);
  const cancelled = useMemo(() => isCancelledStatus(paymentStatus), [paymentStatus]);

  const sellerPrepApproved = useMemo(
    () => lower(workflow?.sellerPrep?.status) === "approved",
    [workflow]
  );
  const buyerCommitted = useMemo(
    () => lower(workflow?.buyerApproval?.status) === "approved",
    [workflow]
  );

  const canStartPayment = useMemo(() => {
    if (!workflow) return false;
    if (!sellerPrepApproved || !buyerCommitted) return false;
    if (!isAwaitingPaymentStage) return false;
    if (paid) return false;
    if (!amountDue) return false;
    if (pending) return false;
    return true;
  }, [workflow, sellerPrepApproved, buyerCommitted, isAwaitingPaymentStage, paid, pending, amountDue]);

  const canRetry = useMemo(() => {
    if (!workflow) return false;
    if (!sellerPrepApproved || !buyerCommitted) return false;
    if (!isAwaitingPaymentStage) return false;
    if (paid) return false;
    if (!amountDue) return false;
    return failed || cancelled;
  }, [workflow, sellerPrepApproved, buyerCommitted, isAwaitingPaymentStage, paid, failed, cancelled, amountDue]);

  const refreshWorkflow = useCallback(async ({ silent = false } = {}) => {
    if (!tradeId) return;
    if (!silent) setRefreshing(true);
    try {
      const wfSnap = await getDoc(doc(db, "TradeWorkflows", tradeId));
      setWorkflow(wfSnap.exists() ? wfSnap.data() : null);
    } catch (e) {
      if (!silent) toast.error("Failed to refresh payment status.");
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, [tradeId]);

  useEffect(() => {
    const load = async () => {
      if (!tradeId) {
        toast.error("Invalid checkout link.");
        navigate("/");
        return;
      }

      if (!currentUser?.uid) {
        navigate("/login", { state: { from: `/checkout/${tradeId}` } });
        return;
      }

      setLoading(true);

      try {
        const tradeSnap = await getDoc(doc(db, "Trades", tradeId));
        const wfSnap = await getDoc(doc(db, "TradeWorkflows", tradeId));

        if (!tradeSnap.exists()) {
          toast.error("Trade not found.");
          navigate("/");
          return;
        }

        const wf = wfSnap.exists() ? wfSnap.data() : null;

        if (!wf) {
          toast.info("Payment is not available yet. Please follow the workflow steps first.");
          navigate(`/trade/${tradeId}?tab=workflow`);
          return;
        }

        const buyerUid = norm(wf?.buyerUid) || norm(tradeSnap.data()?.buyerUid) || null;
        if (!buyerUid || buyerUid !== currentUser.uid) {
          toast.error("You are not authorized to pay for this trade.");
          navigate(`/trade/${tradeId}?tab=workflow`);
          return;
        }

        setTrade({ id: tradeSnap.id, ...tradeSnap.data() });
        setWorkflow(wf);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load payment details.");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeId, currentUser?.uid]);

  // UX-only: interpret return/cancel markers (does not mark paid)
  useEffect(() => {
    const sp = new URLSearchParams(location.search || "");
    const pf = lower(sp.get("pf") || sp.get("status") || "");
    if (!pf) return;

    if (pf === "return" || pf === "success") {
      toast.info("Payment submitted. Awaiting PayFast confirmation (ITN)…");
      refreshWorkflow({ silent: true });
    } else if (pf === "cancel" || pf === "cancelled") {
      toast.info("Payment cancelled. You can retry when ready.");
      refreshWorkflow({ silent: true });
    } else if (pf === "fail" || pf === "failed") {
      toast.info("Payment failed. You can retry when ready.");
      refreshWorkflow({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, tradeId]);

  // Auto-refresh while pending
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => refreshWorkflow({ silent: true }), 6000);
    return () => clearInterval(t);
  }, [pending, refreshWorkflow]);

  const onPayWithPayfast = async () => {
    if (!tradeId) return;

    if (!workflow) {
      toast.info("Workflow not ready. Please return to the trade workflow.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }
    if (!sellerPrepApproved) {
      toast.info("Seller evidence is not yet approved by the platform.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }
    if (!buyerCommitted) {
      toast.info("Please confirm the trade before payment.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }
    if (!isAwaitingPaymentStage) {
      toast.info("Payment is not open for this trade yet. Please follow the workflow steps.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }
    if (paid) {
      toast.success("Payment already confirmed.");
      return;
    }
    if (pending) {
      toast.info("Payment is pending confirmation. Please wait or refresh.");
      return;
    }
    if (!canStartPayment && !canRetry) {
      toast.info("Payment is not available yet. Please follow the workflow steps.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }

    setStartingPayment(true);
      try {
        const res = await callFn("createPayfastPayment", { tradeId });
        const data = res?.data || {};
        const pf = data?.payfast || {};
        const fields = pf?.fields;

        const serverMode = pf?.mode
          ? lower(pf.mode)
          : typeof pf?.sandbox === "boolean"
            ? pf.sandbox
              ? "sandbox"
              : "live"
            : "";

        const endpoint =
          pf?.endpoint ||
          (PAYFAST_MODE === "live"
            ? PAYFAST_ENDPOINTS.live
            : PAYFAST_ENDPOINTS.sandbox);

        if (PAYFAST_MODE === "live" && serverMode === "sandbox") {
          toast.error("PayFast backend is still in sandbox mode. Check PAYFAST_SANDBOX secret.");
          return;
        }

        if (PAYFAST_MODE === "live" && endpoint.includes("sandbox.payfast.co.za")) {
          toast.error("PayFast is still redirecting to sandbox. Check backend and frontend PayFast settings.");
          return;
        }
        if (!endpoint || !fields) {
          toast.error("PayFast setup incomplete. Missing redirect details.");
          return;
        }

        // ✅ Canonical mPaymentId (prefer server response, then fields)
        const mPaymentId =
          norm(data?.payment?.mPaymentId) || norm(fields?.m_payment_id);

        toast.info("Redirecting to PayFast…");

        // Optional: don't block redirect on refresh (reduces “flash”)
        // refreshWorkflow({ silent: true }).catch(() => {});

        postToGateway({
          endpoint,
          fields,
          tradeId,
          mPaymentId,
        });
      } catch (e) {
        console.error("Checkout initiation failed", { provider: "payfast" });
        toast.error("Unable to start the PayFast checkout. Please try again.");
      } finally {
        setStartingPayment(false);
      }
  };

  const onPayWithTradeSafe = async () => {
    if (!tradeId) return;

    if (!workflow) {
      toast.info("Workflow not ready. Please return to the trade workflow.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }
    if (!sellerPrepApproved) {
      toast.info("Seller evidence is not yet approved by the platform.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }
    if (!buyerCommitted) {
      toast.info("Please confirm the trade before payment.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }
    if (!isAwaitingPaymentStage) {
      toast.info("Payment is not open for this trade yet. Please follow the workflow steps.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }
    if (paid) {
      toast.success("Payment already confirmed.");
      return;
    }
    if (pending) {
      toast.info("Payment is pending confirmation. Please wait or refresh.");
      return;
    }
    if (!canStartPayment && !canRetry) {
      toast.info("Payment is not available yet. Please follow the workflow steps.");
      navigate(`/trade/${tradeId}?tab=workflow`);
      return;
    }

    setStartingPayment(true);
    try {
      const res = await callFn("startTradeSafeSandboxCheckout", { tradeId });
      const data = res?.data || {};
      const redirectUrl = data?.redirectUrl;

      if (!isSafeTradeSafeRedirectUrl(redirectUrl)) {
        toast.error("Checkout link did not pass safety validation.");
        return;
      }

      toast.info("Redirecting to secure payment…");
      window.location.assign(redirectUrl);
    } catch (e) {
      console.error("Checkout initiation failed", { provider: "tradesafe" });
      toast.error("Unable to start the secure TradeSafe checkout. Please try again.");
    } finally {
      setStartingPayment(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 840, margin: "40px auto", padding: 20 }}>
        <div style={{ padding: 18, border: "1px solid #e6e6e6", borderRadius: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 900 }}>
            {amountDue ? moneyZAR(amountDue) : "Loading…"}
          </div>
          <div style={{ marginTop: 8, opacity: 0.8 }}>Preparing your payment summary.</div>
        </div>
      </div>
    );
  }

  if (!trade) return null;

  const statusLabel = paid
    ? "Paid"
    : pending
      ? "Pending confirmation"
      : failed
        ? "Failed"
        : cancelled
          ? "Cancelled"
          : sellerPrepApproved && buyerCommitted && isAwaitingPaymentStage
            ? "Ready to pay"
            : "Pending prerequisites";

  const providerLabel = useTradeSafeSandbox ? "TradeSafe Sandbox" : "PayFast";

  const statusHint = paid
    ? useTradeSafeSandbox
      ? "Payment has been confirmed. Return to the workflow to continue."
      : "Payment has been confirmed by PayFast ITN. Return to the workflow to continue."
    : pending
      ? useTradeSafeSandbox
        ? "Payment is awaiting TradeSafe confirmation. This may take a moment—refresh if needed."
        : "Payment is awaiting PayFast confirmation (ITN). This may take a moment—refresh if needed."
      : failed
        ? useTradeSafeSandbox
          ? "The payment failed. You can retry when ready."
          : "The payment failed. You can retry using PayFast."
        : cancelled
          ? useTradeSafeSandbox
            ? "The payment was cancelled. You can retry when ready."
            : "The payment was cancelled. You can retry using PayFast."
          : sellerPrepApproved && buyerCommitted && isAwaitingPaymentStage
            ? useTradeSafeSandbox
              ? "You may proceed with the secure TradeSafe sandbox payment."
              : "You may proceed with PayFast payment."
            : "Complete the prior workflow steps before payment is available.";

  const onStartPayment = useTradeSafeSandbox ? onPayWithTradeSafe : onPayWithPayfast;

  return (
    <div style={{ maxWidth: 920, margin: "40px auto", padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.2 }}>
            Checkout
            <span style={{ fontSize: 14, fontWeight: 700, marginLeft: 10, opacity: 0.7 }}>({providerLabel})</span>
          </div>
          <div style={{ marginTop: 6, opacity: 0.8 }}>
            Review the summary and complete payment for your trade.
          </div>
        </div>

        <div style={{ padding: "8px 12px", borderRadius: 999, border: "1px solid #e6e6e6", fontWeight: 700, fontSize: 13, background: "#fff", whiteSpace: "normal", wordBreak: "break-word", maxWidth: "100%" }}>
          {statusLabel}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        {/* Left */}
        <div style={{ border: "1px solid #e6e6e6", borderRadius: 16, background: "#fff", padding: 18, flex: "1.2 1 0", minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>Order summary</div>

          <div style={{ border: "1px solid #f0f0f0", borderRadius: 14, padding: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, border: "1px solid #f0f0f0", display: "grid", placeItems: "center", fontSize: 20, background: "#fafafa" }}>
              💳
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 15, wordBreak: "break-word" }}>{trade.title || "Untitled item"}</div>
              <div style={{ marginTop: 4, opacity: 0.8, fontSize: 13 }}>
                Trade ID: <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{tradeId}</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
                Provider: <strong>{paymentProvider || (useTradeSafeSandbox ? "tradesafe" : "payfast")}</strong>
              </div>
            </div>

            <div style={{ textAlign: "right", minWidth: 0, wordBreak: "break-word" }}>
              <div style={{ fontSize: 12, opacity: 0.75 }}>Amount due</div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{moneyZAR(amountDue)}</div>
            </div>
          </div>

          {/* Breakdown of amounts */}
          <div style={{ marginTop: 16, border: "1px solid #f0f0f0", borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>Payment breakdown</div>

            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
              <span style={{ opacity: 0.8 }}>Trade amount:</span>
              <span style={{ fontWeight: 600 }}>
               {moneyZAR(
                  workflow?.payment?.itemAmountZar ||
                  workflow?.cash?.itemAmountZar ||
                  workflow?.cash?.amountGross ||
                  workflow?.cashDeal?.itemAmountZar ||
                  workflow?.cashDeal?.amountZar ||
                  Math.max(
                    Number(amountDue || 0) - Number(workflow?.payment?.deliveryFeeZar || 0),
                    0
                  )
                )}
              </span>
            </div>

            {workflow?.payment?.deliveryFeeZar && workflow?.payment?.deliveryFeeZar > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                <span style={{ opacity: 0.8 }}>Delivery fee:</span>
                <span style={{ fontWeight: 600 }}>
                  {moneyZAR(workflow.payment.deliveryFeeZar)}
                </span>
              </div>
            )}

            <div style={{ borderTop: "1px solid #e6e6e6", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 4, fontWeight: 800 }}>
              <span>Total:</span>
              <span>{moneyZAR(amountDue)}</span>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 14, borderRadius: 14, background: "#fafafa" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Status</div>
            <div style={{ opacity: 0.85 }}>{statusHint}</div>

            {!paid && (
              <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>
                <div>Prerequisites:</div>
                <div>• Seller evidence approved: <strong>{sellerPrepApproved ? "Yes" : "No"}</strong></div>
                <div>• Buyer confirmed trade: <strong>{buyerCommitted ? "Yes" : "No"}</strong></div>
                <div>• Stage is awaiting payment: <strong>{isAwaitingPaymentStage ? "Yes" : "No"}</strong></div>
              </div>
            )}
          </div>

          <div style={{ marginTop: 14, border: "1px solid #f0f0f0", borderRadius: 14, padding: 14 }}>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>
              {paid ? "Payment Completed" : "Complete Payment"}
            </div>
            <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.45, marginBottom: 12 }}>
              {paid
                ? "Your payment has been confirmed. You can now continue with the trade workflow."
                : useTradeSafeSandbox
                  ? "Review the breakdown above and continue to the secure TradeSafe sandbox payment page."
                  : "Review the breakdown above and confirm to proceed with PayFast payment. You will be redirected to complete payment securely."
              }
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {paid ? (
                <button type="button" className="tod-btn tod-btn--success nz-touch-min" onClick={() => navigate(`/trade/${tradeId}?tab=workflow`)}>
                  ➜ Continue to Trade
                </button>
              ) : (
                <button
                  type="button"
                  className="tod-btn tod-btn--success nz-touch-min"
                  onClick={onStartPayment}
                  disabled={startingPayment || (!canStartPayment && !canRetry)}
                  style={{
                    opacity: startingPayment || (!canStartPayment && !canRetry) ? 0.6 : 1,
                    cursor: startingPayment || (!canStartPayment && !canRetry) ? "not-allowed" : "pointer",
                  }}
                >
                  {startingPayment
                    ? useTradeSafeSandbox
                      ? "⏳ Preparing secure payment…"
                      : "⏳ Redirecting to PayFast…"
                    : pending
                      ? "⏳ Payment Processing"
                      : failed || cancelled
                        ? "↻ Retry Payment"
                        : useTradeSafeSandbox
                          ? "Continue to secure payment"
                          : "Confirm & Pay with PayFast"}
                </button>
              )}

              <button type="button" className="tod-btn nz-touch-min" onClick={() => navigate(`/trade/${tradeId}?tab=workflow`)}>
                ⬅ Back to Trade
              </button>
            </div>

            {pending && (
              <div style={{ marginTop: 10, fontSize: 12, opacity: 0.78 }}>
                Payment is being processed. This page will update automatically once confirmed.
              </div>
            )}
          </div>

          <div style={{ marginTop: 14, fontSize: 12, opacity: 0.75 }}>
            {useTradeSafeSandbox
              ? "Payment status is confirmed server-side via TradeSafe callback; return/cancel pages are UX only."
              : "Payment status is confirmed server-side via ITN; return/cancel pages are UX only."}
          </div>
        </div>

        {/* Right */}
        <div style={{ border: "1px solid #e6e6e6", borderRadius: 16, background: "#fff", padding: 18, flex: "0.8 1 0", minWidth: 0, height: "fit-content" }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>Secure checkout</div>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 14, background: "#fafafa" }}>
              <div style={{ fontWeight: 800 }}>🔒 Platform-led protection</div>
              <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13, lineHeight: 1.45 }}>
                {useTradeSafeSandbox
                  ? "Funds are held securely by TradeSafe until the trade completes successfully."
                  : "Payment becomes effective only after PayFast ITN confirmation is received by the platform."}
              </div>
            </div>

            <div style={{ padding: 12, borderRadius: 14, background: "#fafafa" }}>
              <div style={{ fontWeight: 800 }}>🧾 Clear status tracking</div>
              <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13, lineHeight: 1.45 }}>
                You can refresh status anytime. Pending payments will auto-refresh periodically.
              </div>
            </div>

            <div style={{ padding: 12, borderRadius: 14, background: "#fafafa" }}>
              <div style={{ fontWeight: 800 }}>⚙️ {useTradeSafeSandbox ? "Server callback = source of truth" : "ITN = source of truth"}</div>
              <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13, lineHeight: 1.45 }}>
                {useTradeSafeSandbox
                  ? "Return/cancel redirects are for user experience. The server-side TradeSafe callback finalizes payment and stage transition."
                  : "Return/cancel redirects are for user experience. The server ITN endpoint finalizes payment and stage transition."}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 12, borderRadius: 14, background: "#fafafa" }}>
            <div style={{ fontWeight: 800 }}>Next step after payment</div>
            <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13, lineHeight: 1.45, wordBreak: "break-word" }}>
              Once paid is confirmed, the workflow moves to <strong>ready_for_inspection</strong>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutStub;